<script setup>
import { InformationCircleIcon } from "@heroicons/vue/24/solid";
import { toString, truncate } from "@js/utils/string";
import "tippy.js/animations/scale.css";
import { computed, h, ref } from "vue";
import { useTippy } from "vue-tippy";

const props = defineProps({
  text: {
    type: String,
    required: true,
  },
  tooltipPlacement: {
    type: String,
    required: false,
    default: "right",
  },
  tooltipIcon: {
    required: false,
    default: InformationCircleIcon,
  },
  tooltipContent: {
    type: [String, Object],
    required: false,
    default: null,
  },
  tooltipColor: {
    type: String,
    required: false,
    default: null,
  },
  align: {
    type: String,
    required: false,
    default: "left",
  },
  color: {
    type: String,
    required: false,
    default: null,
  },
  length: {
    type: Number,
    required: false,
    default: 50,
  },
  lineThrough: {
    type: Boolean,
    required: false,
    default: false,
  },
  title: {
    type: String,
    required: false,
    default: null,
  },
  minWidth: {
    type: Number,
    required: false,
    default: null,
  },
  maxWidth: {
    type: Number,
    required: false,
    default: null,
  },
  displayAsNumber: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const tooltipButton = ref(null);

const tooltipContent = computed(() => {
  if (
    props.tooltipContent !== null &&
    props.tooltipContent !== "" &&
    typeof props.tooltipContent !== "undefined"
  ) {
    if (typeof props.tooltipContent === "string") {
      return h("div", {
        class:
          "max-h-32 w-96 overflow-y-auto rounded bg-white px-2 py-1 drop-shadow wysiwyg",
        innerHTML: props.tooltipContent,
      });
    }

    return h(
      "div",
      {
        class:
          "max-h-32 w-96 overflow-y-auto rounded bg-white px-2 py-1 drop-shadow wysiwyg",
      },
      [props.tooltipContent],
    );
  }

  return h("div");
});

useTippy(tooltipButton, {
  animation: "scale",
  placement: props.tooltipPlacement,
  interactive: true,
  interactiveBorder: 10,
  content: tooltipContent.value,
});
</script>

<template>
  <div
    class="relative inline-flex h-full items-center p-4"
    :class="{
      'justify-start': props.align === 'left',
      'justify-center text-center': props.align === 'center',
      'justify-end': props.align === 'right',
      'text-lg font-bold': props.displayAsNumber,
    }"
    :style="{
      minWidth: `${props.minWidth}px`,
      maxWidth: `${props.maxWidth}px`,
    }"
  >
    <div
      :class="{
        'line-through': props.lineThrough,
        'text-red-500': props.color === 'red',
        'text-green-500': props.color === 'green',
      }"
      :title="props.title"
      v-html="truncate(toString(props.text), props.length)"
    ></div>

    <div
      v-if="
        props.tooltipContent !== null &&
        props.tooltipContent !== '' &&
        typeof props.tooltipContent !== 'undefined'
      "
      class="relative flex items-center justify-center pl-1"
    >
      <button ref="tooltipButton" type="button">
        <component
          :is="props.tooltipIcon"
          class="size-5 cursor-help"
          :class="{
            'text-indigo-600': props.tooltipColor === null,
            'text-red-500': props.tooltipColor === 'red',
            'text-orange-500': props.tooltipColor === 'orange',
            'text-yellow-500': props.tooltipColor === 'yellow',
            'text-green-500': props.tooltipColor === 'green',
          }"
        />
      </button>
    </div>
  </div>
</template>
